import { useCallback } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { produce } from 'immer'

import { SupportedChainId } from '~/constants/chains'
import { QUERY_KEYS } from '~/constants/react-query'
import { useServerRequests } from '~/hooks/useRequests'
import { useCookieStore } from '~/store/cookieStore'
import {
  AuthData,
  BackendTokenDict,
  IAddress,
  Multichain,
  OrgContact,
} from '~/types'

export type DashboardData = {
  contacts: OrgContact[]
  tokenWhitelist: Multichain<BackendTokenDict>
}

// Stable identities so consumers reading these before the dashboard loads get
// a consistent empty value instead of a fresh object on every render.
const EMPTY_TOKEN_WHITELIST: Multichain<BackendTokenDict> = {}
const EMPTY_CONTACTS: OrgContact[] = []

const aggregateDashboard = (authData: AuthData): DashboardData => {
  const tokenWhitelist = authData.settings.tokenWhitelist.reduce(
    (acc, backendToken) => {
      const chainId = parseInt(backendToken.chainId) as SupportedChainId
      acc[chainId] = acc[chainId] ?? {}
      acc[chainId][backendToken.address] = { ...backendToken, chainId }
      return acc
    },
    {} as Multichain<BackendTokenDict>,
  )

  return {
    contacts: authData.contacts ?? EMPTY_CONTACTS,
    tokenWhitelist,
  }
}

const selectContacts = (data: DashboardData): OrgContact[] => data.contacts

export const useDashboard = <TData = DashboardData>(
  select?: (data: DashboardData) => TData,
) => {
  const { serverGetRequest } = useServerRequests()
  const authToken = useCookieStore((s) => s.authToken)
  const activeOrgId = useCookieStore((s) => s.activeOrgId)
  const removeActiveOrgId = useCookieStore((s) => s.removeActiveOrgId)

  const fetchDashboard = useCallback(async (): Promise<DashboardData> => {
    let shouldRetryWithoutOrg = false

    const requestDashboard = async (orgId?: string) => {
      const result = await serverGetRequest<AuthData>({
        path: '/api/user/teams-dashboard',
        params: orgId !== undefined ? { orgId } : {},
        onError: (error) => {
          // We passed an org id the current user does not have access to. Clear
          // it and remake the request so it loads their personal org instead.
          if (error.response?.status === 403 && orgId !== undefined) {
            removeActiveOrgId()
            shouldRetryWithoutOrg = true
          }
        },
      })
      return result?.data
    }

    // One request normally; a second (without an org) only if the first 403'd
    // and cleared the org above.
    let authData = await requestDashboard(activeOrgId)
    if (!authData && shouldRetryWithoutOrg) {
      authData = await requestDashboard()
    }
    if (!authData) throw new Error('Failed to fetch dashboard')

    return aggregateDashboard(authData)
  }, [activeOrgId, serverGetRequest, removeActiveOrgId])

  return useQuery({
    queryKey: [QUERY_KEYS.DASHBOARD, activeOrgId],
    queryFn: fetchDashboard,
    enabled: !!authToken,
    // Fetch only when the org/token changes (and on a fresh load, since the
    // dashboard isn't persisted), never on focus or reconnect. Explicit
    // invalidateQueries/setQueryData still update it.
    staleTime: Infinity,
    select,
  })
}

export const useTokenWhitelist = (): Multichain<BackendTokenDict> => {
  const { data } = useDashboard((dashboard) => dashboard.tokenWhitelist)
  return data ?? EMPTY_TOKEN_WHITELIST
}

export const useOrgContacts = (): {
  contacts: OrgContact[]
  isLoading: boolean
} => {
  const { data, isLoading } = useDashboard(selectContacts)
  return { contacts: data ?? EMPTY_CONTACTS, isLoading }
}

// Optimistic cache edits for the dashboard's contacts. Keyed by the cookie org
// so the key matches useDashboard's query.
export const useDashboardUpdates = () => {
  const queryClient = useQueryClient()
  const activeOrgId = useCookieStore((s) => s.activeOrgId)

  const upsertContact = useCallback(
    (contact: OrgContact) => {
      queryClient.setQueryData<DashboardData>(
        [QUERY_KEYS.DASHBOARD, activeOrgId],
        (data) =>
          data
            ? produce(data, (draft) => {
                const index = draft.contacts.findIndex(
                  (existing) => existing.address === contact.address,
                )
                if (index === -1) draft.contacts.push(contact)
                else draft.contacts[index] = contact
              })
            : data,
      )
    },
    [queryClient, activeOrgId],
  )

  const removeContact = useCallback(
    (address: IAddress) => {
      queryClient.setQueryData<DashboardData>(
        [QUERY_KEYS.DASHBOARD, activeOrgId],
        (data) =>
          data
            ? produce(data, (draft) => {
                draft.contacts = draft.contacts.filter(
                  (contact) => contact.address !== address,
                )
              })
            : data,
      )
    },
    [queryClient, activeOrgId],
  )

  return { upsertContact, removeContact }
}
